import { useEffect, useRef, useState } from 'react';
import type { GeoJSONSource, Map as MapLibreMap, MapMouseEvent, StyleSpecification } from 'maplibre-gl';
import type { Zone } from '@popcart/core/v1';
import { tokens } from '@popcart/tokens';
// The shared style lives in @popcart/ui, whose index pulls React Native; import the style file alone.
import { popcartTrackingStyle, styleFromApi } from '../../../../../packages/ui/src/map/style';
import { DEFAULT_CENTER, DEFAULT_ZOOM, boundsOf, draftGeoJSON, openRing, zonesGeoJSON, type LngLat } from './lib.js';
import { addKitIcons } from '../../ui/mapIcons';
import 'maplibre-gl/dist/maplibre-gl.css';

const c = tokens.colour;

/**
 * The zones on the PopCart map (MapLibre web, the shared `popcartTrackingStyle`, the one PopCart map). Saved zones are Cart
 * Blue outlines (inactive ones Ink-500); the zone being drawn is Pop Yellow with an Ink edge and
 * Ink-edged points. While `drawing`, a click adds a point; clicking a saved zone selects it.
 * MapLibre loads on demand, so the page (and its tests) never pull WebGL in up front.
 */
export function ZoneMap({ zones, editingId, points, drawing, onAddPoint, onSelect }: { zones: Zone[]; editingId: string | null; points: LngLat[]; drawing: boolean; onAddPoint(p: LngLat): void; onSelect(zoneId: string): void }) {
  const box = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const framed = useRef(false);
  // Latest callbacks for the map's own listeners (bound once).
  const live = useRef({ drawing, onAddPoint, onSelect });
  live.current = { drawing, onAddPoint, onSelect };

  useEffect(() => {
    let cancelled = false;
    let map: MapLibreMap | null = null;
    // MapLibre runs its tile work in a module worker; under Vite the worker file is served on its
    // own URL (the pre-bundled library can't find it relative to itself).
    void Promise.all([import('maplibre-gl'), import('maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url')])
      .then(([{ Map, NavigationControl, setWorkerUrl }, { default: workerUrl }]) => {
        if (cancelled || !box.current) return;
        setWorkerUrl(workerUrl);
        map = new Map({ container: box.current, style: styleFromApi(popcartTrackingStyle, window.location.origin) as unknown as StyleSpecification, center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM, attributionControl: { compact: true } });
        // The style's place icons, shop pins and one-way arrows (native maps kit).
        addKitIcons(map);
        map.addControl(new NavigationControl({ showCompass: false }), 'top-right');
        map.on('load', () => {
          if (!map) return;
          map.addSource('zones', { type: 'geojson', data: zonesGeoJSON([]) });
          map.addSource('draft', { type: 'geojson', data: draftGeoJSON([]) });
          map.addLayer({ id: 'zones-fill', type: 'fill', source: 'zones', paint: { 'fill-color': ['case', ['get', 'active'], c.core.ultra, c.neutral.ink500], 'fill-opacity': ['case', ['get', 'active'], 0.18, 0.06] } });
          map.addLayer({ id: 'zones-line', type: 'line', source: 'zones', paint: { 'line-color': ['case', ['get', 'active'], c.core.ultra, c.neutral.ink500], 'line-width': 3 } });
          map.addLayer({
            id: 'zones-label',
            type: 'symbol',
            source: 'zones',
            layout: { 'text-field': ['get', 'name'], 'text-font': ['Noto Sans Bold'], 'text-size': 13 },
            paint: { 'text-color': c.core.ink, 'text-halo-color': c.core.cream, 'text-halo-width': 2 },
          });
          map.addLayer({ id: 'draft-fill', type: 'fill', source: 'draft', filter: ['==', ['get', 'kind'], 'fill'], paint: { 'fill-color': c.core.volt, 'fill-opacity': 0.35 } });
          map.addLayer({ id: 'draft-line', type: 'line', source: 'draft', filter: ['==', ['get', 'kind'], 'line'], paint: { 'line-color': c.core.ink, 'line-width': 3 } });
          map.addLayer({
            id: 'draft-points',
            type: 'circle',
            source: 'draft',
            filter: ['==', ['get', 'kind'], 'point'],
            paint: { 'circle-radius': ['case', ['get', 'first'], 8, 6], 'circle-color': c.core.volt, 'circle-stroke-color': c.core.ink, 'circle-stroke-width': 3 },
          });
          setReady(true);
        });
        map.on('click', (e: MapMouseEvent) => {
          if (!map) return;
          if (live.current.drawing) {
            live.current.onAddPoint([e.lngLat.lng, e.lngLat.lat]);
            return;
          }
          const hit = map.queryRenderedFeatures(e.point, { layers: ['zones-fill'] })[0];
          const id = hit?.properties?.id;
          if (typeof id === 'string') live.current.onSelect(id);
        });
        map.on('error', () => undefined);
        mapRef.current = map;
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      map?.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    (map.getSource('zones') as GeoJSONSource | undefined)?.setData(zonesGeoJSON(zones, editingId) as never);
    // Frame the zones once, as soon as there are any (they may arrive after the map loads).
    const b = framed.current ? null : boundsOf(zones.map((z) => openRing(z.polygon)));
    if (b) {
      map.fitBounds(b, { padding: 48, duration: 0, maxZoom: 16 });
      framed.current = true;
    }
  }, [ready, zones, editingId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    (map.getSource('draft') as GeoJSONSource | undefined)?.setData(draftGeoJSON(points) as never);
  }, [ready, points]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    map.getCanvas().style.cursor = drawing ? 'crosshair' : '';
  }, [ready, drawing]);

  // Editing an existing zone: frame it.
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !editingId) return;
    const z = zones.find((x) => x.id === editingId);
    const b = z ? boundsOf([openRing(z.polygon)]) : null;
    if (b) map.fitBounds(b, { padding: 64, maxZoom: 17 });
  }, [ready, editingId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="zn-map-wrap">
      <div ref={box} className="zn-map" role="application" aria-label={drawing ? 'Map. Click to add a point to the zone.' : 'Map of delivery zones'} />
      {failed ? (
        <p className="zn-map-msg pill st-warning err-pill" role="status" style={{ whiteSpace: 'normal' }}>
          The map didn't load. Zones still work from the list.
        </p>
      ) : !ready ? (
        <p className="zn-map-msg t-monocap muted" role="status">
          Loading the map…
        </p>
      ) : null}
    </div>
  );
}
